import { HttpClient } from '@angular/common/http';
import { ODataArea } from '@app/core/services/odata/types/odata-area.type';
import { ODataFilterBuilder } from '@app/core/services/odata/builders/odata-filter-builter.class';
import { ODataQueryBuilder } from '@app/core/services/odata/builders/odata-query-builder.class';
import { catchError, map, Observable, of, retry, shareReplay, timer } from 'rxjs';
import { ODataQueryOptions } from '@app/core/services/odata/interfaces/odata-query-options.interface';
import { ODataFilterInput } from '@app/core/services/odata/types/odata-filter-input.type';
import { ODataResponse } from '@app/core/services/odata/interfaces/odata-response.interface';
import { PaginationOptions } from '@app/core/services/odata/interfaces/odata-paginate-options.interface';
import { tap } from 'rxjs/operators';
import { SortParam } from '@app/core/constants/sort-param';

/**
 * Client for a specific OData area (NTS or DMC)
 */
export class ODataAreaClient {
  private readonly cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(
    private readonly http: HttpClient,
    private readonly baseUrl: string,
    private readonly area: ODataArea
  ) {}

  /**
   * Create a new filter builder
   *
   * @returns A new ODataFilterBuilder instance
   *
   * @example
   * const filter = this.odataService.dmc.filter()
   *   .equals('status', 'Active')
   *   .greaterThan('createdDate', new Date('2024-01-01'));
   */
  public filter(): ODataFilterBuilder {
    return new ODataFilterBuilder();
  }

  /**
   * Create a new query builder
   *
   * @returns A new ODataQueryBuilder instance
   *
   * @example
   * const query = this.odataService.dmc.query()
   *   .filter(this.odataService.filter().equals('status', 'Active'))
   *   .orderBy('name')
   *   .paginate(0, 50);
   */
  public query(): ODataQueryBuilder {
    return new ODataQueryBuilder();
  }

  /**
   * Get a collection of entities with OData query options
   *
   * @param entitySet - The name of the OData entity set
   * @param filterOrOptions - Filter builder, query builder, or query options object
   * @param additionalOptions - Additional query options (when using filter builder)
   * @returns Observable of OData response with entities
   *
   * @example
   * // Using filter builder
   * const filter = this.odataService.filter().equals('status', 'Active');
   * this.odataService.dmc.getCollection<MeteringPoint>('MeteringPoints', filter)
   *   .subscribe(response => console.log(response.value));
   *
   * @example
   * // Using filter builder with additional options
   * const filter = this.odataService.filter().equals('status', 'Active');
   * this.odataService.dmc.getCollection<MeteringPoint>('MeteringPoints', filter, {
   *   $orderby: 'createdDate desc',
   *   $top: 50,
   *   $count: true
   * }).subscribe(response => {
   *   console.log(response.value); // entities
   *   console.log(response['@odata.count']); // total count
   * });
   *
   * @example
   * // Using query builder
   * const query = this.odataService.query()
   *   .filter(this.odataService.filter().equals('status', 'Active'))
   *   .orderBy('name')
   *   .paginate(0, 50)
   *   .count();
   * this.odataService.dmc.getCollection<MeteringPoint>('MeteringPoints', query)
   *   .subscribe(response => console.log(response));
   *
   * @example
   * // Using raw options object
   * this.odataService.dmc.getCollection<MeteringPoint>('MeteringPoints', {
   *   $filter: "status eq 'Active'",
   *   $orderby: 'name',
   *   $top: 50
   * }).subscribe(response => console.log(response));
   */
  public getCollection<T>(
    entitySet: string,
    filterOrOptions?: ODataFilterInput,
    additionalOptions?: Omit<ODataQueryOptions, '$filter'>
  ): Observable<ODataResponse<T>> {
    let queryOptions: ODataQueryOptions | undefined;

    if (filterOrOptions instanceof ODataFilterBuilder) {
      queryOptions = {
        ...additionalOptions,
        $filter: filterOrOptions.isEmpty() ? undefined : filterOrOptions.build()
      };
    } else if (filterOrOptions instanceof ODataQueryBuilder) {
      queryOptions = filterOrOptions.build();
    } else {
      queryOptions = filterOrOptions;
    }

    const url = this.buildUrl(`${entitySet}`, queryOptions);
    return this.http.get<ODataResponse<T>>(url);
  }

  /**
   * Get a count of entities matching an optional filter.
   * Uses $count=true&$top=0 so no entities are returned — only @odata.count in the response.
   *
   * @param entitySet - The name of the OData entity set
   * @param filter - Optional filter builder
   * @param customParams - Optional custom query parameters prepended before OData system params
   * @returns Observable of the entity count
   */
  public getCount(
    entitySet: string,
    filter?: ODataFilterBuilder,
    customParams?: Record<string, string>
  ): Observable<number> {
    const query = this.query().count().top(0);

    if (filter && !filter.isEmpty()) {
      query.filter(filter);
    }

    if (customParams) {
      for (const [key, value] of Object.entries(customParams)) {
        query.param(key, value);
      }
    }

    return this.getCollection<never>(entitySet, query).pipe(
      map(response => response?.['@odata.count'] ?? 0)
    );
  }

  /**
   * Get a collection with caching
   *
   * @param entitySet - The name of the OData entity set
   * @param filterOrOptions - Filter builder, query builder, or query options object
   * @param additionalOptions - Additional query options (when using filter builder)
   * @param cacheTTL - Cache time-to-live in milliseconds (default: 5 minutes)
   * @returns Observable of OData response with entities (cached)
   *
   * @example
   * this.odataService.dmc.getCollectionCached<MeteringPoint>(
   *   'MeteringPoints',
   *   this.odataService.filter().equals('status', 'Active')
   * ).subscribe(response => console.log(response));
   */
  public getCollectionCached<T>(
    entitySet: string,
    filterOrOptions?: ODataFilterBuilder | ODataQueryBuilder | ODataQueryOptions,
    additionalOptions?: Omit<ODataQueryOptions, '$filter'>,
    cacheTTL?: number
  ): Observable<ODataResponse<T>> {
    const cacheKey = `${this.area}-${entitySet}-${JSON.stringify(filterOrOptions)}-${JSON.stringify(additionalOptions)}`;
    const cached = this.cache.get(cacheKey);
    const ttl = cacheTTL || this.CACHE_TTL;

    if (cached && Date.now() - cached.timestamp < ttl) {
      return of(cached.data);
    }

    return this.getCollection<T>(entitySet, filterOrOptions, additionalOptions).pipe(
      tap(data => {
        this.cache.set(cacheKey, { data, timestamp: Date.now() });
      }),
      shareReplay(1)
    );
  }

  /**
   * Get a paginated and sorted collection with fixed primary sort and optional default sort
   *
   * @param entitySet - The name of the OData entity set
   * @param pagination - Page and page size
   * @param fixedSort - Primary sort that always comes first
   * @param userSort - User-provided sort (optional)
   * @param defaultSort - Default sort to use when user doesn't provide one (optional)
   * @param filter - Optional filter builder or options
   * @returns Observable of OData response with entities
   *
   * @example
   * this.odataService.dmc.getPaginatedWithFixedSort(
   *   ODataType.disconnected,
   *   { page: 1, pageSize: 50 },
   *   { sortField: 'isCancelledBySupplier', sortDirection: SortDir.DESC },
   *   userSort,
   *   { sortField: 'occurrence', sortDirection: SortDir.ASC }
   * );
   */
  public getPaginatedWithFixedSort<T>(
    entitySet: string,
    pagination: { page: number; pageSize: number },
    fixedSort: SortParam,
    userSort?: SortParam,
    defaultSort?: SortParam,
    filter?: ODataFilterBuilder | ODataQueryOptions,
    customParams?: Record<string, string>
  ): Observable<ODataResponse<T>> {
    const query = this.query()
      .paginate(pagination.page, pagination.pageSize)
      .count();

    // Determine which sort to use (user or default)
    const secondarySort = userSort ?? defaultSort;

    // Build orderBy string
    let orderBy: string;
    if (secondarySort) {
      orderBy = `${fixedSort.sortField} ${fixedSort.sortDirection}, ${secondarySort.sortField} ${secondarySort.sortDirection}`;
    } else {
      orderBy = `${fixedSort.sortField} ${fixedSort.sortDirection}`;
    }

    query.orderBy(orderBy);

    // Apply filter if provided
    if (filter) {
      if (filter instanceof ODataFilterBuilder) {
        query.filter(filter);
      } else if (filter.$filter) {
        query.filter(filter.$filter);
      }
    }

    // Apply custom query parameters
    if (customParams) {
      for (const [key, value] of Object.entries(customParams)) {
        query.param(key, value);
      }
    }

    return this.getCollection<T>(entitySet, query);
  }

  /**
   * Get a collection with automatic retry on failure
   *
   * @param entitySet - The name of the OData entity set
   * @param filterOrOptions - Filter builder, query builder, or query options object
   * @param additionalOptions - Additional query options (when using filter builder)
   * @param retryCount - Number of retry attempts (default: 3)
   * @returns Observable of OData response with entities
   *
   * @example
   * this.odataService.dmc.getCollectionWithRetry<MeteringPoint>(
   *   'MeteringPoints',
   *   this.odataService.filter().equals('status', 'Active'),
   *   undefined,
   *   3
   * ).subscribe(response => console.log(response));
   */
  public getCollectionWithRetry<T>(
    entitySet: string,
    filterOrOptions?: ODataFilterBuilder | ODataQueryBuilder | ODataQueryOptions,
    additionalOptions?: Omit<ODataQueryOptions, '$filter'>,
    retryCount: number = 3
  ): Observable<ODataResponse<T>> {
    return this.getCollection<T>(entitySet, filterOrOptions, additionalOptions).pipe(
      retry({
        count: retryCount,
        delay: (error, retryAttempt) => {
          console.warn(`Retry attempt ${retryAttempt} for ${entitySet}`, error);
          return timer(Math.pow(2, retryAttempt) * 1000); // Exponential backoff
        }
      }),
      catchError(error => {
        console.error(`Failed after ${retryCount} retries for ${entitySet}`, error);
        throw error;
      })
    );
  }

  /**
   * Get a paginated collection with optional sorting - convenience method
   *
   * @param entitySet - The name of the OData entity set
   * @param pagination - Pagination and optional sort options
   * @param filter - Optional filter builder or options
   * @returns Observable of OData response with entities
   *
   * @example
   * this.odataService.dmc.getPaginated<MeteringPoint>(
   *   'MeteringPoints',
   *   { page: 1, pageSize: 50, sort: { sortField: 'name', sortDirection: 'desc' } },
   *   this.odataService.filter().equals('status', 'Active')
   * ).subscribe(response => console.log(response));
   */
  public getPaginated<T>(
    entitySet: string,
    pagination: PaginationOptions,
    filter?: ODataFilterBuilder | ODataQueryOptions
  ): Observable<ODataResponse<T>> {
    const query = this.query()
      .paginateAndSort(pagination)
      .count();

    if (filter) {
      if (filter instanceof ODataFilterBuilder) {
        query.filter(filter);
      } else if (filter.$filter) {
        query.filter(filter.$filter);
      }
    }

    return this.getCollection<T>(entitySet, query);
  }

  /**
   * Clear cache for this area
   *
   * @example
   * this.odataService.dmc.clearCache();
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear specific cache entry
   *
   * @param entitySet - The entity set to clear cache for
   *
   * @example
   * this.odataService.dmc.clearCacheFor('MeteringPoints');
   */
  public clearCacheFor(entitySet: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(entitySet)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get a single entity by key
   *
   * @param entitySet - The name of the OData entity set
   * @param key - The entity key (can be string GUID or number)
   * @param options - Optional query options (select, expand)
   * @returns Observable of the entity
   *
   * @example
   * // Get by GUID
   * this.odataService.dmc.getEntity<MeteringPoint>(
   *   'MeteringPoints',
   *   '14c36070-b2c1-43cf-856c-0d60c81a68e9'
   * ).subscribe(entity => console.log(entity));
   *
   * @example
   * // Get with expansion
   * this.odataService.dmc.getEntity<MeteringPoint>(
   *   'MeteringPoints',
   *   '14c36070-b2c1-43cf-856c-0d60c81a68e9',
   *   { $expand: ['connectionPoint', 'meter'] }
   * ).subscribe(entity => console.log(entity));
   *
   * @example
   * // Get with field selection
   * this.odataService.dmc.getEntity<MeteringPoint>(
   *   'MeteringPoints',
   *   '14c36070-b2c1-43cf-856c-0d60c81a68e9',
   *   { $select: ['id', 'name', 'status'] }
   * ).subscribe(entity => console.log(entity));
   */
  public getEntity<T>(
    entitySet: string,
    key: string | number,
    options?: Omit<ODataQueryOptions, '$filter' | '$orderby' | '$top' | '$skip'>
  ): Observable<T> {
    const url = this.buildUrl(`${entitySet}(${this.formatKey(key)})`, options);
    return this.http.get<T>(url);
  }

  /**
   * Create a new entity
   *
   * @param entitySet - The name of the OData entity set
   * @param entity - The entity data to create
   * @returns Observable of the created entity
   *
   * @example
   * const newMeteringPoint = {
   *   name: 'MP-12345',
   *   status: 'Active',
   *   type: 'Consumption'
   * };
   *
   * this.odataService.dmc.create<MeteringPoint>('MeteringPoints', newMeteringPoint)
   *   .subscribe(created => console.log('Created:', created));
   */
  public create<T>(
    entitySet: string,
    entity: Partial<T>
  ): Observable<T> {
    const url = `${this.baseUrl}/${this.area}/odata/${entitySet}`;
    return this.http.post<T>(url, entity).pipe(
      tap(() => this.clearCacheFor(entitySet))
    );
  }

  /**
   * Update an entity (PATCH - partial update)
   *
   * @param entitySet - The name of the OData entity set
   * @param key - The entity key
   * @param entity - The partial entity data to update
   * @returns Observable of the updated entity
   *
   * @example
   * this.odataService.dmc.update<MeteringPoint>(
   *   'MeteringPoints',
   *   '14c36070-b2c1-43cf-856c-0d60c81a68e9',
   *   { status: 'Inactive' }
   * ).subscribe(updated => console.log('Updated:', updated));
   */
  public update<T>(
    entitySet: string,
    key: string | number,
    entity: Partial<T>
  ): Observable<T> {
    const url = `${this.baseUrl}/${this.area}/odata/${entitySet}(${this.formatKey(key)})`;
    return this.http.patch<T>(url, entity).pipe(
      tap(() => this.clearCacheFor(entitySet))
    );
  }

  /**
   * Replace an entity (PUT - full replacement)
   *
   * @param entitySet - The name of the OData entity set
   * @param key - The entity key
   * @param entity - The complete entity data
   * @returns Observable of the replaced entity
   *
   * @example
   * const meteringPoint: MeteringPoint = {
   *   id: '14c36070-b2c1-43cf-856c-0d60c81a68e9',
   *   name: 'MP-12345',
   *   status: 'Active',
   *   type: 'Consumption',
   *   createdDate: new Date()
   * };
   *
   * this.odataService.dmc.replace<MeteringPoint>(
   *   'MeteringPoints',
   *   '14c36070-b2c1-43cf-856c-0d60c81a68e9',
   *   meteringPoint
   * ).subscribe(replaced => console.log('Replaced:', replaced));
   */
  public replace<T>(
    entitySet: string,
    key: string | number,
    entity: T
  ): Observable<T> {
    const url = `${this.baseUrl}/${this.area}/odata/${entitySet}(${this.formatKey(key)})`;
    return this.http.put<T>(url, entity).pipe(
      tap(() => this.clearCacheFor(entitySet))
    );
  }

  /**
   * Delete an entity
   *
   * @param entitySet - The name of the OData entity set
   * @param key - The entity key
   * @returns Observable of void (completes when deleted)
   *
   * @example
   * this.odataService.dmc.delete('MeteringPoints', '14c36070-b2c1-43cf-856c-0d60c81a68e9')
   *   .subscribe(() => console.log('Deleted successfully'));
   */
  public delete(
    entitySet: string,
    key: string | number
  ): Observable<void> {
    const url = `${this.baseUrl}/${this.area}/odata/${entitySet}(${this.formatKey(key)})`;
    return this.http.delete<void>(url).pipe(
      tap(() => this.clearCacheFor(entitySet))
    );
  }

  /**
   * Execute a custom OData function
   *
   * @param functionName - The name of the OData function
   * @param parameters - Optional function parameters
   * @returns Observable of the function result
   *
   * @example
   * // Function without parameters
   * this.odataService.dmc.executeFunction<number>('GetTotalCount')
   *   .subscribe(count => console.log('Total:', count));
   *
   * @example
   * // Function with parameters
   * this.odataService.dmc.executeFunction<MeteringPoint[]>('GetMeteringPointsByDate', {
   *   startDate: new Date('2024-01-01'),
   *   endDate: new Date('2024-12-31')
   * }).subscribe(results => console.log('Results:', results));
   */
  public executeFunction<T>(
    functionName: string,
    parameters?: Record<string, any>
  ): Observable<T> {
    let url = `${this.baseUrl}/${this.area}/odata/${functionName}`;

    if (parameters) {
      const params = Object.entries(parameters)
        .map(([key, value]) => `${key}=${this.formatValue(value)}`)
        .join(',');
      url += `(${params})`;
    }

    return this.http.get<T>(url);
  }

  /**
   * Execute a custom OData action
   *
   * @param actionName - The name of the OData action
   * @param body - Optional action body/parameters
   * @returns Observable of the action result
   *
   * @example
   * // Action without body
   * this.odataService.dmc.executeAction<void>('RecalculateAll')
   *   .subscribe(() => console.log('Recalculation started'));
   *
   * @example
   * // Action with body
   * this.odataService.dmc.executeAction<ProcessResult>('ProcessMeteringPoints', {
   *   meteringPointIds: ['id1', 'id2', 'id3'],
   *   processType: 'Validation'
   * }).subscribe(result => console.log('Result:', result));
   */
  public executeAction<T>(
    actionName: string,
    body?: any
  ): Observable<T> {
    const url = `${this.baseUrl}/${this.area}/odata/${actionName}`;
    return this.http.post<T>(url, body || {});
  }

  /**
   * Build URL with query options
   */
  private buildUrl(path: string, options?: ODataQueryOptions): string {
    let url = `${this.baseUrl}/${this.area}/odata/${path}`;

    if (!options) {
      return url;
    }

    const params: string[] = [];

    if (options.customParams) {
      for (const [key, value] of Object.entries(options.customParams)) {
        params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }

    if (options.$filter) {
      params.push(`$filter=${encodeURIComponent(options.$filter)}`);
    }

    if (options.$orderby) {
      params.push(`$orderby=${encodeURIComponent(options.$orderby)}`);
    }

    if (options.$top !== undefined) {
      params.push(`$top=${options.$top}`);
    }

    if (options.$skip !== undefined) {
      params.push(`$skip=${options.$skip}`);
    }

    if (options.$select && options.$select.length > 0) {
      params.push(`$select=${options.$select.join(',')}`);
    }

    if (options.$expand && options.$expand.length > 0) {
      params.push(`$expand=${options.$expand.join(',')}`);
    }

    if (options.$count) {
      params.push('$count=true');
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return url;
  }

  /**
   * Format key for URL (handles strings and numbers)
   */
  private formatKey(key: string | number): string {
    return typeof key === 'string' ? `'${key}'` : key.toString();
  }

  /**
   * Format value for OData function parameters
   */
  private formatValue(value: any): string {
    if (typeof value === 'string') {
      return `'${value}'`;
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value.toString();
  }
}
